import { Injectable } from '@angular/core';
import { DemandeVirementDC } from '../models/demandeVirement.model';
import { MouvementFinancierOutput, MouvementFinancierOutput2, MouvementsFinanciersService } from '../../../../../openapi/build';
import { NotificationService } from '../../../services/alerte/notification.service';
import { DemandeVirementStatut } from '../../../commun/Constants';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmVirementRefusComponent } from '../modals/confirm-virement-refus/confirm-virement-refus.component';
import { DemandeMouvementComponent } from '../../demande-mouvement/demande-mouvement.component';
import { DetailDemandeRefuseeComponent } from '../../liste-historique-virement/modals/detail-demande-refusee/detail-demande-refusee.component';
import { DemandeMouvementSensEnum } from '../../demande-mouvement/demande-mouvement-enum';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VirementService {
  private promiseAttenteVirement = (uuid: string): Promise<DemandeVirementDC> =>
    new Promise<DemandeVirementDC>((resolve, reject): void => {
      this.virementInterneService.putApiMouvementsFinanciersAttente(uuid).subscribe(
        (virement: DemandeVirementDC): void => {
          resolve(virement);
        },
        (error: any): void => {
          console.log('promiseAttenteVirement', error);
          reject(error);
        }
      );
    });

  private promiseValidationVirement = (uuid: string): Promise<DemandeVirementDC> =>
    new Promise<DemandeVirementDC>((resolve, reject): void => {
      this.virementInterneService.putApiMouvementsFinanciersValider(uuid).subscribe(
        (virement: DemandeVirementDC): void => {
          resolve(virement);
        },
        (error: any): void => {
          console.log('promiseValidationVirement', error);
          reject(error);
        }
      );
    });

  private promiseRefusVirement = (uuid: string, motif: string): Promise<DemandeVirementDC> =>
    new Promise<DemandeVirementDC>((resolve, reject): void => {
      this.virementInterneService.putApiMouvementsFinanciersRefuser(uuid, { motifRefus: motif }).subscribe(
        (virement: DemandeVirementDC): void => {
          resolve(virement);
        },
        (error: any): void => {
          console.log('promiseRefusVirement', error);
          reject(error);
        }
      );
    });

  constructor(
    private virementInterneService: MouvementsFinanciersService,
    private notificationService: NotificationService,
    public dialog: MatDialog
  ) {}

  listerDemandeVirement(statut: string = DemandeVirementStatut.ATraiter): Promise<MouvementFinancierOutput[]> {
    return firstValueFrom(this.virementInterneService.getApiMouvementsFinanciersList(undefined, undefined, statut)) as Promise<
      MouvementFinancierOutput[]
    >;
  }

  afficherDemandeVirement(uuid: string): Promise<MouvementFinancierOutput2> {
    return firstValueFrom(this.virementInterneService.getApiMouvementsFinanciersItem(uuid)) as Promise<MouvementFinancierOutput2>;
  }

  afficherDetailVirementRefus(virement: DemandeVirementDC): Promise<void> {
    // console.log(virement);
    return new Promise<void>((resolve, reject): Promise<void> => {
      const dialogDetail = this.dialog.open(DetailDemandeRefuseeComponent, {
        data: {
          virementInterne: virement
        },
        maxWidth: '95%'
      });

      return firstValueFrom(dialogDetail.afterClosed()).then(
        (result: any): void => {
          // Fermeture de la boite de dialogue
          // undefined : fermeture modal via la touche "échap" et le bouton "close"
          resolve();
        },
        (error: any): void => {
          console.log('afficherDetailVirementRefus', error);
          reject(error);
        }
      );
    });
  }

  afficherDetailVirement(virement: DemandeVirementDC, mode: string = 'validate'): Promise<boolean | DemandeVirementDC[]> {
    return new Promise<DemandeVirementDC[] | boolean>((resolve, reject): Promise<void> => {
      const dialogDetail = this.dialog.open(DemandeMouvementComponent, {
        data: {
          projet: virement.ligneBudgetCible?.projet, // TODO : récupérer projet...
          ligneBudget: virement.ligneBudgetCible,
          virementInterne: virement,
          mode,
          type: virement.type,
          sens: DemandeMouvementSensEnum.entrant
        },
        maxWidth: '95%'
      });

      return firstValueFrom(dialogDetail.afterClosed()).then(
        (result: any): void => {
          // Fermeture de la boite de dialogue
          // undefined : fermeture modal via la touche "échap" et le bouton "close"
          if (result !== undefined) {
            // Appel à l'API
            if (result === 'valider') {
              resolve(this.validerVirements([virement]));
            } else if (result === 'refuser') {
              resolve(this.refuserVirements([virement]));
            } else {
              resolve(false);
            }
          } else {
            // Appel à l'API
            resolve(false);
          }
        },
        (error: any): void => {
          reject(error);
        }
      );
    });
  }

  refuserVirements(virements: DemandeVirementDC[]): Promise<DemandeVirementDC[]> {
    return new Promise<DemandeVirementDC[]>((resolve, reject): Promise<void> => {
      const statut = DemandeVirementStatut.Refus;
      const promiseVirements: Promise<DemandeVirementDC>[] = [];
      const dialogRefus = this.dialog.open(ConfirmVirementRefusComponent, {
        data: {
          nb: virements.length
        }
      });

      return firstValueFrom(dialogRefus.afterClosed()).then(
        (result: string | undefined): void => {
          // console.log('afterClosed', result);
          // undefined : fermeture modal via la touche "échap" et les boutons "non" et "close"
          // //false : fermeture modal via les boutons "non" et "close"
          if (result !== undefined /* && result !== 'false'*/) {
            // Fermeture de la boite de dialogue
            // console.log('motif refus', result);
            virements
              .map((virement: DemandeVirementDC) => {
                return {
                  uuid: virement.uuid,
                  motif: result,
                  statut
                };
              })
              .forEach((virement: any): void => {
                if (virement.uuid && virement.statut) {
                  promiseVirements.push(this.promiseRefusVirement(virement.uuid, virement.motif).catch((error: any): any => error));
                } else {
                  this.notificationService.info('Mouvement incomplet pour le traitement');
                }
              });

            // Appel à l'API
            resolve(this.modifierStatutVirements(promiseVirements));
          } else {
            // Appel à l'API
            resolve(this.listerDemandeVirement());
          }
        },
        (error: any): void => {
          console.log('refuserVirements', error);
          reject(error);
        }
      );
    });
  }

  validerVirements(virements: DemandeVirementDC[]): Promise<DemandeVirementDC[]> {
    const statut = DemandeVirementStatut.Valide;
    const promiseVirements: Promise<DemandeVirementDC>[] = [];

    virements
      .map((virement: DemandeVirementDC) => {
        return {
          uuid: virement.uuid,
          statut
        };
      })
      .forEach((virement: any): void => {
        if (virement.uuid && virement.statut) {
          promiseVirements.push(this.promiseValidationVirement(virement.uuid).catch((error: any): any => error));
        } else {
          this.notificationService.info('Mouvement incomplet pour le traitement');
        }
      });

    // Appels à l'API
    return this.modifierStatutVirements(promiseVirements);
  }

  attenteVirements(virements: DemandeVirementDC[]): Promise<DemandeVirementDC[]> {
    const statut = DemandeVirementStatut.Valide;
    const promiseVirements: Promise<DemandeVirementDC>[] = [];

    virements
      .map((virement: DemandeVirementDC) => {
        return {
          uuid: virement.uuid,
          statut
        };
      })
      .forEach((virement: any): void => {
        if (virement.uuid && virement.statut) {
          promiseVirements.push(this.promiseAttenteVirement(virement.uuid).catch((error: any): any => error));
        } else {
          this.notificationService.info('Mouvement incomplet pour le traitement');
        }
      });

    // Appels à l'API
    return this.modifierStatutVirements(promiseVirements);
  }

  private async modifierStatutVirements(promiseVirements: Promise<DemandeVirementDC>[]): Promise<DemandeVirementDC[]> {
    const nbSelection = promiseVirements.length;
    const retour: any[] = [];
    let nbErreur = 0;
    let nbSuppression = 0;

    // console.log('DEBUT FOR');
    for (const promise of promiseVirements) {
      // console.log('Appel promesse');
      retour.push(await promise);
      // console.log('Retour promesse');
    }
    // console.log('FIN FOR');
    // console.log('RETOUR', retour);
    for (const res of retour) {
      if (res.statut) {
        nbSuppression++;
      } else {
        nbErreur++;
      }
    }
    // console.log('NOTIF');
    if (nbSelection === 1) {
      // Une seule demande
      if (nbErreur === 0) {
        this.notificationService.successEdit('La demande a été mise à jour');
      } else {
        this.notificationService.errorEdit("La demande n'a pu être mise à jour");
      }
    } else {
      // Plusieurs demandes
      if (nbErreur === 0) {
        this.notificationService.successEdit(nbSuppression.toString() + '/' + nbSelection.toString() + ' demandes ont été mises à jour');
      } else {
        this.notificationService.errorEdit(nbErreur.toString() + '/' + nbSelection.toString() + " demandes n'ont pu être mises à jour");
      }
    }
    // console.log('FIN ACTION');
    return this.listerDemandeVirement();
  }

  private modifierStatutVirements_PromiseAll(promiseVirements: Promise<DemandeVirementDC>[]): Promise<DemandeVirementDC[]> {
    const nbSelection = promiseVirements.length;

    // Appels à l'API
    return Promise.all(promiseVirements)
      .then((virements: DemandeVirementDC[]): void => {
        // console.log('promise.all', virements);
        let nbErreur = 0;
        let nbSuppression = 0;
        if (virements.length > 0) {
          virements.forEach((virement: DemandeVirementDC): void => {
            if (virement.statut) {
              nbSuppression++;
            } else {
              nbErreur++;
            }
          });
        }
        if (nbSelection === 1) {
          // Une seule demande
          if (nbErreur === 0) {
            this.notificationService.successEdit('La demande a été mise à jour');
          } else {
            this.notificationService.errorEdit("La demandes n'a pu être mise à jour");
          }
        } else {
          // Plusieurs demandes
          if (nbErreur === 0) {
            this.notificationService.successEdit(
              nbSuppression.toString() + '/' + nbSelection.toString() + ' demandes ont été mises à jour'
            );
          } else {
            this.notificationService.errorEdit(nbErreur.toString() + '/' + nbSelection.toString() + " demandes n'ont pu être mises à jour");
          }
        }
      })
      .then(() => {
        return this.listerDemandeVirement();
      })
      .catch((error: any) => {
        console.log('promiseVirementService', error);
        this.notificationService.errorEdit("Des demandes n'ont pu être mises à jour");
        // return [];
        // // return new Promise<DemandeVirementDC[]>((resolve) => []);
        return this.listerDemandeVirement();
        // }).finally(() => {
        //   console.log('finally: void...');
        //   return this.listerDemandeVirement();
      });
  }
}
